'use client';

import { useState } from "react";
import { AccountWithRole } from "@/types/account";
import { Button } from "@/components/ui/button";
import { ChevronDown, User, Users, LogOut, UserCog } from "lucide-react";
import Link from "next/link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useSignout, useSwitchAccount } from "@/utils/client/db";
import { User as SbUser } from "@supabase/supabase-js";
import { CampaignInvitation } from "@/types/campaign";


interface AccountSwitcherProps {
    user: SbUser | null | undefined;
    activeAccount: AccountWithRole | null | undefined;
    accounts: AccountWithRole[] | null | undefined;
    campaignInvitations: CampaignInvitation[] | null | undefined;
    isAdmin?: boolean;
    isSuperAdmin?: boolean;
    matchingInvitations?: any[];
}

export default function AccountSwitcher({ activeAccount, accounts, user, campaignInvitations }: AccountSwitcherProps) {
    const signout = useSignout();
    const switchAccount = useSwitchAccount();

    // Function to get invitation count for a specific account
    const getInvitationCount = (accountId: string) => {
        if (!campaignInvitations) return 0;
        return campaignInvitations.filter(invitation => invitation.account_id === accountId).length;
    };

    // Get notification count for active account
    const activeAccountNotifications = activeAccount 
        ? getInvitationCount(activeAccount.account.id) 
        : 0;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 relative">
                    {activeAccount?.account.personal_account ? (
                        <User className="h-4 w-4" />
                    ) : (
                        <Users className="h-4 w-4" />
                    )}
                    <span>{activeAccount?.account.slug}</span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                    {activeAccountNotifications > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                            {activeAccountNotifications}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                <div className="p-2 text-sm font-medium">
                    Switch account
                </div>
                <DropdownMenuSeparator />
                {accounts?.map((ar: AccountWithRole) => {
                    const invitationCount = getInvitationCount(ar.account.id);
                    
                    return (
                        <DropdownMenuItem 
                            key={ar.account.id}
                            className={`flex items-center gap-2 cursor-pointer ${
                            activeAccount?.account.id === ar.account.id ? 'bg-gray-100 text-black' : ''
                            }`}
                            onClick={() => switchAccount(ar, user)}
                        >
                            <div className="relative z-50">
                                {ar.account.personal_account ? (
                                    <User className="h-4 w-4" />
                                ) : (
                                    <Users className="h-4 w-4" />
                                )}
                            </div>
                            
                            {ar.account.personal_account? 
                                <span className="text-xs ml-auto">Personal</span>
                                :
                                <span className="text-xs ml-auto">{ar.account.name}</span>
                            }
                            {invitationCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-3 h-3 flex items-center justify-center text-xs">
                                        {invitationCount}
                                    </span>
                                )}
                        </DropdownMenuItem>
                    );
                })}

                {/* User Email Display */}
                <DropdownMenuSeparator />
                {user?.email && (
                    <div className="px-2 py-1 text-xs text-gray-500 truncate">
                        {user.email}
                    </div>
                )}

                {/* Logout Button - Desktop */}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                    className="flex items-center gap-2 cursor-pointer text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => signout()}
                >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export function AccountSwitcherMobile({ 
    activeAccount, 
    accounts, 
    user, 
    campaignInvitations,
    isAdmin,
    isSuperAdmin,
    matchingInvitations = []
}: AccountSwitcherProps) {
    const signout = useSignout();
    const switchAccount = useSwitchAccount();
    
    // Add state to control the sheet's open/closed state
    const [isOpen, setIsOpen] = useState(false);
    
    // Function to get invitation count for a specific account
    const getInvitationCount = (accountId: string) => {
        if (!campaignInvitations) return 0;
        return campaignInvitations.filter(invitation => invitation.account_id === accountId).length;
    };
    
    // Get notification count for active account
    const activeAccountNotifications = activeAccount 
        ? getInvitationCount(activeAccount.account.id) 
        : 0;
    
    // Check if there are notifications
    const hasNotifications = matchingInvitations.length > 0;
    
    // Function to handle switching accounts and closing the sheet
    const handleSwitchAccount = (ar: AccountWithRole, user: any) => {
        switchAccount(ar, user);
        setIsOpen(false);
    };
    
    // Function to handle logout and close the sheet
    const handleSignout = () => {
        signout();
        setIsOpen(false);
    };
    
    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="h-8 w-8 relative">
                    <UserCog className="h-5 w-5" />
                    <span className="sr-only">Toggle menu</span>
                    {activeAccountNotifications > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-3 h-3 flex items-center justify-center text-xs">
                            {activeAccountNotifications}
                        </span>
                    )}
                </Button>
            </SheetTrigger>
            <SheetContent side="right">
                {/* Add SheetTitle for accessibility */}
                <SheetTitle className="sr-only">Account Menu</SheetTitle>
                
                {/* Mobile Account Switcher */}
                <div className="py-4 border-b border-gray-200">
                    <div className="flex items-center gap-2 mb-4">
                        {activeAccount?.account.personal_account ? (
                            <User className="h-5 w-5" />
                        ) : (
                            <Users className="h-5 w-5" />
                        )}
                        <span className="font-medium">{activeAccount?.account.slug}</span>
                    </div>
                    
                    <div className="text-sm font-medium text-gray-500 mb-2">
                        Switch account
                    </div>
                    
                    <div className="space-y-1">
                        {accounts?.map((ar: AccountWithRole) => {
                            const invitationCount = getInvitationCount(ar.account.id);
                            return (
                                <button
                                    key={ar.account.id}
                                    className={`flex items-center gap-2 w-full text-left p-2 rounded ${
                                        activeAccount?.account.id === ar.account.id
                                        ? 'bg-gray-100 text-black' 
                                        : 'hover:bg-gray-50 hover:text-black'
                                    }`}
                                    onClick={() => handleSwitchAccount(ar, user)}
                                >
                                    <div className="relative">
                                        {ar.account.personal_account ? (
                                            <User className="h-4 w-4" />
                                        ) : (
                                            <Users className="h-4 w-4" />
                                        )}
                                        {invitationCount > 0 && (
                                            <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-3 h-3 flex items-center justify-center text-xs">
                                                {invitationCount}
                                            </span>
                                        )}
                                    </div>
                                    <span>{ar.account.slug}</span>
                                    {ar.account.personal_account ? (
                                        <span className="text-xs ml-auto">Personal</span>
                                    ) : (
                                        <span className="text-xs ml-auto">{ar.account.name}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
                
                {/* Navigation Links */}
                <div className="flex flex-col space-y-3 mt-6">
                    {/* Dashboard link - always shown */}
                    <div className="relative">
                        <Link 
                            href="/protected" 
                            className="flex items-center gap-2 w-full text-base font-medium p-2 hover:bg-gray-50 rounded"
                            onClick={() => setIsOpen(false)}
                        >
                            Dashboard
                        </Link>
                        {hasNotifications && (
                            <span className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                                {matchingInvitations.length}
                            </span>
                        )}
                    </div>
                    
                    {/* Admin link - conditional */}
                    {isAdmin && !activeAccount?.account.personal_account && (
                        <Link 
                            href="/protected/client-admin/setup" 
                            className="flex items-center gap-2 w-full text-base font-medium p-2 hover:bg-gray-50 rounded"
                            onClick={() => setIsOpen(false)}
                        >
                            Admin
                        </Link>
                    )}
                    
                    {/* Personal link - conditional */}
                    {activeAccount?.account.personal_account && (
                        <Link 
                            href="/protected/personal" 
                            className="flex items-center gap-2 w-full text-base font-medium p-2 hover:bg-gray-50 rounded"
                            onClick={() => setIsOpen(false)}
                        >
                            Personal
                        </Link>
                    )}
                    
                    {/* Super Admin link - conditional */}
                    {isSuperAdmin && (
                        <Link 
                            href="/protected/admin" 
                            className="flex items-center gap-2 w-full text-base font-medium p-2 hover:bg-gray-50 rounded"
                            onClick={() => setIsOpen(false)}
                        >
                            Super Admin
                        </Link>
                    )}
                    
                    {/* User Email Display - Mobile */}
                    {user?.email && (
                        <div className="pt-2 mt-2 border-t border-gray-200">
                            <div className="text-xs text-gray-500 p-2 break-all">
                                {user.email}
                            </div>
                        </div>
                    )}
                    
                    {/* Logout Button - Mobile */}
                    <div className="pt-4 mt-4 border-t border-gray-200">
                        <button 
                            className="flex items-center gap-2 text-red-500 font-medium hover:text-red-700 p-2"
                            onClick={handleSignout}
                        >
                            <LogOut className="h-4 w-4" />
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    )
}